import L from 'leaflet';
import SettingsService from './SettingsService';

const LocationService = {
  /**
   * Retrieves all active locations.
   *
   * @returns {Array<StorageLocation> | null} An array of active location objects.
   */
  getActiveLocations: () => {
    const plans = window.TCRS2PluginData?.plans || [];
    const fallbackEmail = SettingsService.getEmailNotifications();

    return plans
      .map(local => ({
        id: local.id,
        // The admin-configured "ID" doubles as the order number used to look up this
        // location's containers via GetFreeSelfStorageContainers.
        orderNumber: local.id,
        name: local.title,
        address: local.desc,
        isActive: local.isActive,
        latitude: local.latitude ? parseFloat(local.latitude) : null,
        longitude: local.longitude ? parseFloat(local.longitude) : null,
        grouped_size_types: local.grouped_size_types || [],
        page_id: local.page_id || null,
        email:
          local.email === '' || local.email === null
            ? fallbackEmail
            : local.email,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  /**
   * Gets the map configuration. from the settings.
   *
   * @returns {Object<{center: [number, number], zoom: number}>} The map configuration.
   */
  getMapConfig: () => {
    const zoom = SettingsService.getMapZoomLevel();
    const coordString = SettingsService.getMapCoordinates();

    let center = [56.2020232, 8.9054418]; // Default: Denmark
    if (coordString.trim() !== '') {
      const [lat, lng] = coordString
        .split(',')
        .map(coord => parseFloat(coord.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        center = [lat, lng];
      }
    }

    return { center, zoom };
  },

  /**
   * Gets the marker icons for the map.
   *
   * @returns {Object<{defaultIcon: L.Icon, selectedIcon: L.Icon}>} The marker icons.
   */
  getMarkerIcons: () => {
    const pluginUrl = SettingsService.getPluginUrl();

    const defaultIcon = L.icon({
      iconUrl: `${pluginUrl}marker.png`,
      iconSize: [20, 35],
      iconAnchor: [10, 35],
      popupAnchor: [0, -35],
    });

    const selectedIcon = L.icon({
      iconUrl: `${pluginUrl}marker-selected.png`,
      iconSize: [24, 42],
      iconAnchor: [12, 42],
      popupAnchor: [0, -42],
    });

    return { defaultIcon, selectedIcon };
  },

  /**
   * Builds the HTML content for a location marker's popup.
   *
   * @param {StorageLocation} location - The location object.
   * @returns {string} The HTML content for the popup.
   */
  buildMarkerPopup: location => {
    return `<b>${location.name}</b><br>${location.address}`;
  },

  /**
   * Gets the URL for the plugin.
   * @returns {string} The plugin URL.
   */
  getPluginUrl: () => {
    return SettingsService.getPluginUrl();
  },
};

export default LocationService;
